import React, {
  createContext,
  useState,
  useMemo,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { useQuery, useMutation } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { fetchExpenseClaimDetails } from '../../../store/apis/expenseService';
import {
  validationSchema,
  modalFieldInitialValues,
  initialValues,
  rowSchema,
} from '../Employee/EmployeeExpenseClaim/constants';
import { DateUtil } from '../../../utils';
import { useExpenseClaimUtilCtx } from './ExpenseClaimUtilProvider';

const ExpenseClaimContext = createContext();

export function useExpenseClaimCtx() {
  const ctx = useContext(ExpenseClaimContext);
  if (!ctx) {
    throw new Error(
      'useExpenseClaimCtx must be used within ExpenseClaimProvider',
    );
  }
  return ctx;
}

export function ExpenseClaimProvider({ children }) {
  const { defaultCurrCode, expenseCategories, defaultCurrency } =
    useExpenseClaimUtilCtx();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const claimId = queryParams.get('ID');
  const mode = queryParams.get('Mode');
  const appType = queryParams.get('AppTyp') && +queryParams.get('AppTyp');
  const appRank = queryParams.get('AppRank') && +queryParams.get('AppRank');
  const expApprDetails = queryParams.get('ExpApprDetails');
  const checkMode = () => {
    if (
      mode &&
      mode === 'APPROVE' &&
      appType !== null &&
      appRank !== null &&
      expApprDetails != null
    ) {
      return 'approve';
    }
    if (mode && mode === 'APPROVE' && appType !== null && appRank !== null) {
      return 'view';
    }
    return 'create';
  };
  const [modeType, setModeType] = useState(checkMode() || 'create');
  const thisYear = new Date().getFullYear();
  const thisMonth = new Date().getMonth();
  const { endDate } = DateUtil.getMonthRange(thisMonth, thisYear);
  const [expenseClaimHeaders, setExpenseClaimHeaders] = useState({
    claimNo: '',
    claimDate: DateUtil.formatDateObjToHtmlDate(new Date(endDate)),
    currency: 'AED',
    claimAmt: 0,
    reimburAmt: 0,
    advanceRecovery: 0,
    comments: '',
    advancePaid: 0,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: 'Modal Title',
    dataConfig: null,
    data: [],
  });
  const [viewAttachmentsModalOpen, setViewAttachmentsModalOpen] =
    useState(false);
  const [viewAttachmentSrNo, setViewAttachmentSrNo] = useState(null);
  const [approvalDetails, setApprovalDetails] = useState({
    approvalRank: +appRank || 0,
    approvalType: +appType || 0,
  });
  const [displayModalOpen, setDisplayModalOpen] = useState(false);
  const [displayModalFile, setDisplayModalFile] = useState(null);

  // Utility Function for Temp ID
  const generateTempId = () => Date.now() * -1;

  useEffect(() => {
    if (expApprDetails) {
      const [date, details, requester, coCd] = expApprDetails.split('~');
      setApprovalDetails(prev => ({
        ...prev,
        requestedDate: date,
        requestorId: requester,
        companyCode: coCd.trim(),
        details: details || '',
      }));
    }
  }, [expApprDetails]);

  const getExpenseClaimDetailsMutation = useMutation({
    mutationFn: payload => fetchExpenseClaimDetails(payload),
  });

  const { data: expenseClaimDetailsHeader, isLoading: isHeaderLoading } =
    useQuery({
      queryKey: ['expenseClaimDetailsHeader', claimId],
      queryFn: () =>
        fetchExpenseClaimDetails({
          claimNo: claimId,
          listType: 'HEADER',
        }),
      enabled: !!claimId,
    });

  const { data: expenseClaimDetails, isLoading: isDetailLoading } = useQuery({
    queryKey: ['expenseClaimDetails', claimId, appType, appRank],
    queryFn: () =>
      fetchExpenseClaimDetails({
        claimNo: claimId,
        listType: 'DETAIL',
        appType: appType ?? null,
        appRank: appRank ?? null,
      }),
    enabled: !!claimId,
  });

  const { data: loanDetails, isLoading: isLoanLoading } = useQuery({
    queryKey: ['loanDetail', claimId, expenseClaimHeaders?.employeeId],
    queryFn: () =>
      fetchExpenseClaimDetails({
        claimNo: claimId,
        listType: 'LOAN',
        employeeId: expenseClaimHeaders?.employeeId || '',
      }),
    enabled: !!claimId && !!expenseClaimHeaders?.employeeId,
  });

  const isLoadingAll = isHeaderLoading || isDetailLoading || isLoanLoading;

  // =========================================================

  useEffect(() => {
    if (loanDetails && loanDetails.length > 0) {
      // Compute totat advance recovery
      const totalAdvancePaid = loanDetails.reduce(
        (acc, item) => acc + parseFloat(item?.Balance || 0),
        0,
      );
      setExpenseClaimHeaders(prev => ({
        ...prev,
        advancePaid: totalAdvancePaid || 0,
      }));
    }
  }, [loanDetails]);

  useEffect(() => {
    if (expenseClaimDetailsHeader && expenseClaimDetailsHeader?.length > 0) {
      const headerData = expenseClaimDetailsHeader[0];
      setExpenseClaimHeaders(prev => ({
        ...prev,
        claimNo: headerData?.ClaimNo || '',
        claimDate: DateUtil.formatDateObjToHtmlDate(
          new Date(headerData?.ClaimDt),
        ),
        currency: headerData?.Curr?.trim() || 'AED',
        advanceRecovery: parseFloat(headerData?.ClaimAdvanceRecovery),
        comments: headerData?.Comments || '',
        claimStatus: headerData?.ClaimStatus || '',
        submittedOn: headerData?.ClaimSubmitTime,
        approvedOn: headerData?.ClaimApprovedTime,
        reviewedBy: headerData?.ClaimReviewedBy || '',
        settledOn: headerData?.SettlementDate,
        reviewedOn: headerData?.ClaimReviewTime,
        transNo: headerData?.GLTransNo || '',
        requestedBy: headerData?.['Requested By'] || '',
        employeeId: headerData?.EmpCd?.trim() || '',
        finCoCd: headerData?.FinCoCd?.trim() || '',
      }));
    }
  }, [expenseClaimDetailsHeader]);

  const mapExpenseClaimDetails = details =>
    details.map(detail => ({
      expCategory: detail?.ExpenseCatgory?.trim() || '',
      expType: detail?.ExpenseType?.trim() || '',
      expDate: DateUtil.formatDateObjToHtmlDate(new Date(detail?.ExpenseDate)),
      expRate: parseFloat(detail?.ExRate).toFixed(6) || 0,
      currency: detail?.ExpenseCurr?.trim() || '',
      taxAmount: parseFloat(detail?.TaxAmt) || 0,
      amount: parseFloat(detail?.Amt) || 0,
      remarks: detail?.Remarks || '',
      sorNo: detail?.SORNo?.trim() || '',
      entity: detail?.Entity?.trim() || '',
      countryCode: detail?.Country?.trim() || '',
      dependent: Number(detail?.Dependent),
      expFromDate: detail?.ExpenseFromPrd
        ? DateUtil.formatDateObjToHtmlDate(new Date(detail?.ExpenseFromPrd))
        : '',
      expToDate: detail?.ExpenseToPrd
        ? DateUtil.formatDateObjToHtmlDate(new Date(detail?.ExpenseToPrd))
        : '',
      expCostAlloc: detail?.CostAllocation?.trim() || '',
      expCustomer: detail?.Customer?.trim() || '',
      product: detail?.Product?.trim() || '',
      reimburAmt: parseFloat(detail?.ReimburseAmt) || 0,
      // Additional fields
      srNo: detail?.SrNo || '',
      count: detail?.Count || 0,
    }));

  // =========================================================
  const formMethods = useForm({
    resolver: yupResolver(validationSchema, {
      context: { skipLastRow: true }, // ← drop last row on all trigger()/handleSubmit
    }),
    defaultValues: {
      ...initialValues,
      currency: defaultCurrCode || 'AED',
      entity: defaultCurrency?.[0]?.Entity?.trim() || '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const formFieldArray = useFieldArray({
    control: formMethods.control,
    name: 'claims',
  });

  // Reinitialize modal state for a row index
  const resetRowModalState = React.useCallback(
    rowIndex => {
      Object.entries({
        ...modalFieldInitialValues,
        entity: defaultCurrency?.[0]?.Entity?.trim(),
      }).forEach(([key, value]) => {
        formMethods.setValue(`claims.${rowIndex}.${key}`, value);
      });
    },
    [
      formMethods,
      modalFieldInitialValues,
      defaultCurrency?.[0]?.Entity?.trim(),
    ],
  );

  // Get Row Data
  const getRowData = React.useCallback(
    rowIndex => formMethods.getValues(`claims.${rowIndex}`) || {},
    [formMethods],
  );

  // Add Row
  const addRow = React.useCallback(() => {
    formFieldArray.append({
      ...initialValues,
      currency: defaultCurrCode || '',
      entity: defaultCurrency?.[0]?.Entity?.trim() || '',
    });
  }, [formFieldArray, defaultCurrCode, defaultCurrency?.[0]?.Entity]);

  // Initialize header data
  useEffect(() => {
    if (!defaultCurrCode) return;
    setExpenseClaimHeaders(prev => ({
      ...prev,
      currency: defaultCurrCode,
    }));
  }, [defaultCurrCode]);

  // Compute reimbursement amount for all rows
  const watchedClaims = useWatch({
    control: formMethods.control,
    name: 'claims',
    defaultValue: [],
  });

  useEffect(() => {
    const claims = formMethods.watch('claims') || [];
    let totalClaimAmt = 0;
    claims.forEach(claim => {
      const amtRow = parseFloat(claim.amount) * parseFloat(claim.expRate) || 0;
      totalClaimAmt += amtRow;
    });
    setExpenseClaimHeaders(prev => ({
      ...prev,
      reimburAmt: totalClaimAmt - prev.advanceRecovery,
      claimAmt: totalClaimAmt,
    }));
  }, [watchedClaims, expenseClaimDetailsHeader, expenseClaimDetails]);

  const resetExpenseClaimAll = React.useCallback(() => {
    formFieldArray.replace(
      Array.from({ length: 1 }, () => ({
        ...initialValues,
        currency: defaultCurrCode || '',
        entity: defaultCurrency?.[0]?.Entity?.trim() || '',
      })),
    );
    setExpenseClaimHeaders({
      claimNo: '',
      claimDate: DateUtil.formatDateObjToHtmlDate(new Date(endDate)),
      currency: defaultCurrCode || 'AED',
      claimAmt: 0,
      reimburAmt: 0,
      advanceRecovery: 0,
      comments: '',
    });
  }, [
    formFieldArray,
    defaultCurrCode,
    setExpenseClaimHeaders,
    endDate,
    defaultCurrency?.[0]?.Entity,
  ]);

  const resetExpenseClaim = () => {
    formFieldArray.replace(
      Array.from({ length: 1 }, () => ({
        ...initialValues,
        currency: defaultCurrCode || '',
        entity: defaultCurrency?.[0]?.Entity?.trim() || '',
      })),
    );
  };

  useEffect(() => {
    if (expenseClaimDetails && expenseClaimDetails.length > 0) {
      const mappedDetails = mapExpenseClaimDetails(expenseClaimDetails);
      if (modeType === 'create' && !location.pathname.includes('/detail')) {
        mappedDetails.push({
          ...initialValues,
          currency: defaultCurrCode || '',
          entity: defaultCurrency?.[0]?.Entity?.trim() || '',
        });
      }
      formFieldArray.replace(mappedDetails);
    } else {
      resetExpenseClaim();
    }
  }, [
    expenseClaimDetails,
    expenseCategories,
    defaultCurrency?.[0]?.Entity?.trim(),
  ]);

  /**
   * @param {boolean} focus
   * @returns {Promise<boolean>}
   */
  const runValidationsAndToastErrors = useCallback(
    async (focus = false, istoast = true) => {
      await formMethods.trigger();
      const { errors } = formMethods.formState;
      const claimErrors = errors.claims;
      let hasErrors = false;

      if (Array.isArray(claimErrors)) {
        const rowMessages = claimErrors
          .map((rowError, idx) => {
            if (!rowError) return null;
            hasErrors = true;
            const msgs = Object.values(rowError).map(
              e => e?.message || 'Invalid',
            );
            return `Row ${idx + 1}: ${msgs.join('; ')}`;
          })
          .filter(Boolean);

        if (rowMessages.length && istoast) {
          toast.error(rowMessages.join('\n'), {
            className: 'toast-error',
          });
        }
      }

      if (hasErrors && focus) {
        // scroll & focus the first invalid input
        const el = document.querySelector('.is-invalid');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus();
        }
      }

      return hasErrors;
    },
    [formMethods],
  );

  const validateLastRow = useCallback(
    /**
     * @param {boolean} focus
     * @param {boolean} showToast
     * @returns {Promise<{ isValid: boolean; errors: Record<string,string> }>}
     */
    async (focus = false, showToast = true) => {
      const claims = formMethods.getValues('claims') || [];
      const lastIndex = claims.length - 1;
      if (lastIndex < 0) {
        return { isValid: true, errors: {} };
      }

      // clear any old errors on that last row
      formMethods.clearErrors(`claims.${lastIndex}`);

      try {
        // validate only that row
        await rowSchema.validate(claims[lastIndex], { abortEarly: false });
        return { isValid: true, errors: {} };
      } catch (validationError) {
        // build a map of field → message
        const errorMap = {};
        validationError.inner.forEach(e => {
          errorMap[e.path] = e.message;
          // register with RHF so .is-invalid shows up
          formMethods.setError(`claims.${lastIndex}.${e.path}`, {
            type: 'manual',
            message: e.message,
          });
        });

        // toast a summary if desired
        if (showToast) {
          const msgs = Object.values(errorMap).join('; ');
          toast.error(`Row ${lastIndex + 1}: ${msgs}`, {
            className: 'toast-error',
          });
        }

        // optionally scroll & focus
        if (focus) {
          const el = document.querySelector('.is-invalid');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.focus();
          }
        }

        return { isValid: false, errors: errorMap };
      }
    },
    [formMethods],
  );

  const value = useMemo(
    () => ({
      formMethods,
      formFieldArray,
      resetRowModalState,
      addRow,
      getRowData,
      expenseClaimHeaders,
      setExpenseClaimHeaders,
      getExpenseClaimDetailsMutation,
      isModalOpen,
      setIsModalOpen,
      modalConfig,
      setModalConfig,
      resetExpenseClaimAll,
      setViewAttachmentsModalOpen,
      viewAttachmentsModalOpen,
      viewAttachmentSrNo,
      setViewAttachmentSrNo,
      modeType,
      setModeType,
      approvalDetails,
      runValidationsAndToastErrors,
      validateLastRow,
      isLoadingAll,
      displayModalOpen,
      setDisplayModalOpen,
      displayModalFile,
      setDisplayModalFile,
      generateTempId,
    }),
    [
      formMethods,
      formFieldArray,
      resetRowModalState,
      addRow,
      getRowData,
      expenseClaimHeaders,
      setExpenseClaimHeaders,
      getExpenseClaimDetailsMutation,
      isModalOpen,
      setIsModalOpen,
      modalConfig,
      setModalConfig,
      resetExpenseClaimAll,
      setViewAttachmentsModalOpen,
      viewAttachmentsModalOpen,
      viewAttachmentSrNo,
      setViewAttachmentSrNo,
      modeType,
      setModeType,
      approvalDetails,
      runValidationsAndToastErrors,
      validateLastRow,
      isLoadingAll,
      displayModalOpen,
      setDisplayModalOpen,
      displayModalFile,
      setDisplayModalFile,
      generateTempId,
    ],
  );
  return (
    <ExpenseClaimContext.Provider value={value}>
      {children}
    </ExpenseClaimContext.Provider>
  );
}
